import React, { useState } from 'react';
import { Box, InputBase, SxProps, Theme, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

interface Props {
  value: string | null;
  /** Called with the trimmed text, or null when cleared. Not called when unchanged. */
  onSave: (value: string | null) => void;
  /** Accessible name of the field: "Trip name", "Label for Day 3". */
  label: string;
  /** Shown, muted, when there is no value yet. */
  placeholder?: string;
  textSx?: SxProps<Theme>;
  maxLength?: number;
}

/**
 * Text edited where it stands: tap it (or its pencil), type, Enter or tap
 * away to save, Escape to leave it as it was. Used for names that read as
 * headings, so it looks like text until touched.
 */
export default function InlineEdit({ value, onSave, label, placeholder, textSx, maxLength = 120 }: Props) {
  const [draft, setDraft] = useState<string | null>(null);
  const finish = () => {
    if (draft === null) return;
    const next = draft.trim() || null;
    setDraft(null);
    if (next !== (value ?? null)) onSave(next);
  };

  if (draft !== null) {
    return (
      <InputBase
        autoFocus
        fullWidth
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={finish}
        onKeyDown={(e) => {
          if (e.key === 'Enter') finish();
          if (e.key === 'Escape') setDraft(null);
        }}
        inputProps={{ 'aria-label': label, maxLength }}
        sx={[{ borderBottom: '2px solid', borderColor: 'primary.light' }, ...(Array.isArray(textSx) ? textSx : [textSx])]}
      />
    );
  }
  return (
    <Box
      component="button"
      type="button"
      onClick={() => setDraft(value ?? '')}
      aria-label={value ? `${label}: ${value}. Edit` : `Add ${label.toLowerCase()}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        maxWidth: '100%',
        p: 0,
        border: 0,
        borderBottom: '2px solid transparent',
        bgcolor: 'transparent',
        font: 'inherit',
        textAlign: 'left',
        cursor: 'text',
        '&:hover': { borderColor: 'divider' },
        '&:hover .edit-hint, &:focus-visible .edit-hint': { opacity: 1 },
      }}
    >
      <Typography component="span" sx={[...(Array.isArray(textSx) ? textSx : [textSx]), value ? {} : { color: 'text.disabled', fontStyle: 'italic' }]}>
        {value || placeholder}
      </Typography>
      <EditOutlinedIcon
        className="edit-hint"
        sx={{ fontSize: '1.1em', color: 'text.secondary', opacity: { xs: 1, md: 0.45 }, flex: 'none' }}
      />
    </Box>
  );
}
